import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import ProfileSelector from 'app/components/ProfileSelector';
import getProfileSelectorTitleByFilterOption from 'app/components/profileSelector/utils/getProfileSelectorTitleByFilterOption';
import { useAnalyticsSelection } from 'app/modules/analytics/hooks/useAnalyticsSelection';
import { useAnalyticsContext } from 'app/modules/analytics/context/AnalyticsContext';
import { useAnalyticsInventory } from 'app/modules/analytics/hooks/useAnalyticsInventory';
import useAnalyticsSelectorFilter from 'app/modules/analytics/components/analyticsProfileSelector/hooks/useAnalyticsSelectorFilter';
import AnalyticsProfileSelectorOptions from 'app/modules/analytics/components/analyticsProfileSelector/AnalyticsProfileSelectorOptions';
import AnalyticsProfileSelectorFooter from 'app/modules/analytics/components/analyticsProfileSelector/AnalyticsProfileSelectorFooter';

const AnalyticsProfileSelector: FC = () => {
  const { t } = useTranslation();
  const { pages, profileGroups } = useAnalyticsInventory();
  const { isLoading } = useAnalyticsContext();
  const {
    selectedPage,
    selectedProfileGroup,
    selectedPageId,
    selectedGroupId,
    selectPage,
    selectGroup,
  } = useAnalyticsSelection();

  const {
    sidebarFilterOptions,
    searchQuery,
    setSearchQuery,
    selectedFilterOption,
    selectFilterOption,
    filteredProfiles,
    filteredProfileGroups,
  } = useAnalyticsSelectorFilter(pages, profileGroups);

  const placeholderData = useMemo(() => {
    if (selectedProfileGroup) {
      return [
        {
          name: selectedProfileGroup.name,
          logoSrc: selectedProfileGroup.logo?.src ?? undefined,
        },
      ];
    }

    if (selectedPage) {
      return [
        {
          name: selectedPage.name ?? '',
          type: selectedPage.type,
          logoSrc: selectedPage.logo?.src,
        },
      ];
    }

    return [
      {
        name: t('selectProfile'),
      },
    ];
  }, [selectedPage, selectedProfileGroup, t]);

  if (isLoading) {
    return (
      <div className="tw-w-full" data-cy="analytics_profile-selector_trigger">
        <Skeleton className="tw-h-9 tw-w-full tw-rounded-lg" />
      </div>
    );
  }

  return (
    <ProfileSelector
      placeholderClassName="!tw-max-w-none tw-w-full xl:!tw-w-full 2xl:!tw-w-full 2xl:!tw-max-w-none"
      title={getProfileSelectorTitleByFilterOption(selectedFilterOption)}
      optionsElement={
        <AnalyticsProfileSelectorOptions
          analyticsPages={pages}
          filteredProfiles={filteredProfiles}
          filteredProfileGroups={filteredProfileGroups}
          selectedPageId={selectedPageId}
          selectedGroupId={selectedGroupId}
          onSelectPage={selectPage}
          onSelectGroup={selectGroup}
        />
      }
      filter={{
        searchQuery,
        selectedFilterOption,
        onSearch: setSearchQuery,
        options: sidebarFilterOptions,
        onSelectFilterOption: selectFilterOption,
      }}
      isSelectedAll={false}
      footer={<AnalyticsProfileSelectorFooter />}
      placeholderData={placeholderData}
      dataName={{
        placeholder: 'analytics_profile-selector_trigger',
        options: 'analytics_profile-selector_dropdown',
        filter: 'analytics_profile-management_filter',
      }}
    />
  );
};

export default AnalyticsProfileSelector;
